import { X, Loader2, AlertCircle, BookOpenText } from "lucide-react";
import { PopoverClose } from "@radix-ui/react-popover";
import { useTagDetail } from "../hooks";
import { TagType } from "@/types/mapping";
import { tagKey } from "../utils";
import { ExtendedChip, StandardChip } from "@/components/common/standard-chip";
import { cn } from "@/lib/utils";

interface TagDetailFieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
  isLoading?: boolean;
}

const TagDetailField = ({
  label,
  value,
  isLoading = false,
}: TagDetailFieldProps) => {
  return (
    <div className="flex py-[8px] px-[16px] flex-col items-start self-stretch gap-[8px]">
      <span className="text-[#737373] caption-2-semibold">{label}</span>
      {isLoading ? (
        <div className="flex items-center gap-2">
          <Loader2 className="w-[12px] h-[12px] animate-spin" />
          <span className="text-primary label-1-normal-regular">
            로딩 중...
          </span>
        </div>
      ) : (
        <span className="text-primary label-1-normal-regular w-full break-words">
          {value?.toString() ?? "-"}
        </span>
      )}
    </div>
  );
};

const TagDetailError = () => {
  return (
    <div className="flex py-[16px] px-[16px] flex-col items-center justify-center gap-[8px] self-stretch">
      <AlertCircle className="w-[24px] h-[24px] text-red-500" />
      <span className="text-red-500 label-1-normal-regular text-center">
        태그 상세 정보를 불러오는 중<br />
        오류가 발생했습니다.
      </span>
    </div>
  );
};

interface DetailTriggerProps {
  isOpen: boolean;
}

const TagDetailPopoverTrigger = ({ isOpen }: DetailTriggerProps) => {
  return (
    <div
      className={cn(
        "mapping-detail-button flex w-[28px] h-[28px] p-[4px] items-center justify-center flex-shrink-0 gap-[10px] aspect-square rounded-[4px] bg-white/0 hover:bg-black/[0.08] active:bg-black/[0.16] opacity-0 group-hover:opacity-100 transition-all duration-200",
        isOpen &&
          "bg-blue-50 text-blue-500 hover:bg-blue-50/90 active:bg-blue-50/60 opacity-100",
      )}
    >
      <BookOpenText className="w-[16px] h-[16px] flex-shrink-0 aspect-square" />
    </div>
  );
};

interface TagDetailPopoverContentProps {
  prefix: string;
  name: string;
  label: string;
  type: TagType;
  description: string | null;
  /** 주어지면 롤 기준 설명까지 서버에서 받아온다. */
  roleKey?: string;
}

const TagDetailPopoverContent = ({
  prefix,
  name,
  label,
  type,
  description,
  roleKey,
}: TagDetailPopoverContentProps) => {
  const { data, isLoading, isError } = useTagDetail(
    tagKey(prefix, name),
    roleKey,
  );

  const resolvedDescription = description || data?.detail?.description;
  const basicFields = [
    { label: "prefix", value: prefix },
    { label: "영문라벨", value: name },
  ];
  if (resolvedDescription) {
    basicFields.push({ label: "설명", value: resolvedDescription });
  }

  const detailFields = [
    { label: "Data type", value: data?.detail?.tagSpecificInfo?.dataType },
    // { label: "Unit", value: data?.detail?.tagSpecificInfo?.unit },
    // { label: "소수 자릿수", value: data?.detail?.tagSpecificInfo?.decimals },
    // { label: "정수 자릿수", value: data?.detail?.tagSpecificInfo?.precisions },
    { label: "Balance", value: data?.detail?.tagSpecificInfo?.balance },
    { label: "Period type", value: data?.detail?.tagSpecificInfo?.periodType },
    { label: "Negated", value: data?.detail?.tagSpecificInfo?.negated },
  ];

  return (
    <>
      {/* 헤더 */}
      <div className="flex h-[36px] justify-between items-center self-stretch border-b-[1px] border-b-neutral-100 py-[12px] pl-[16px] pr-[8px]">
        <span className="text-[#171717] label-1-normal-semibold">
          태그 상세정보
        </span>
        <div className="flex w-[22px] h-[22px] py-[4px] px-[8px] justify-center items-center gap-[4px] rounded-[4px] cursor-pointer">
          <PopoverClose asChild>
            <X className="w-[14px] h-[14px] flex-shrink-0 aspect-square text-neutral-500" />
          </PopoverClose>
        </div>
      </div>

      <div className="overflow-y-auto w-full">
        {/* 태그 라벨 */}
        <div className="flex min-h-[100px] p-[16px] flex-col justify-center items-center gap-[8px] self-stretch bg-neutral-100">
          {data?.detail.isExtended ? <ExtendedChip /> : <StandardChip />}
          <span className="text-primary label-1-normal-medium text-center">
            {label}
          </span>
        </div>

        {isError ? (
          <TagDetailError />
        ) : (
          <div className="flex py-[16px] px-0 flex-col items-start self-stretch">
            {/* 기본 정보 */}
            {basicFields.map((field) => (
              <TagDetailField
                key={field.label}
                label={field.label}
                value={field.value}
                isLoading={isLoading && field.label === "설명"}
              />
            ))}
            {type === "LINE_ITEM" && (
              <>
                {/* 구분선 */}
                <div className="flex h-[5px] py-[2px] px-0 items-center gap-[4px] self-stretch">
                  <div className="h-[1px] flex-1 bg-neutral-200" />
                </div>

                {/* 상세 정보 */}
                {detailFields.map((field) => (
                  <TagDetailField
                    key={field.label}
                    label={field.label}
                    value={field.value}
                    isLoading={isLoading}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export { TagDetailPopoverContent, TagDetailPopoverTrigger };
